import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ClipboardCheck,
  Eye,
  FileText,
  GitCompare,
  LockKeyhole,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
} from "lucide-react";

import {
  CanonicalField,
  createPMFormDraft,
  createPMFormDraftReviewSnapshot,
  fetchFormApprovalConcurrence,
  fetchPMFormDrafts,
  fetchPMFormGovernanceRegistry,
  fetchPMReviewProcessOptions,
  FormApprovalConcurrenceState,
  FormApprovalEvidenceSnapshot,
  FormDraftProjection,
  FormDraftSection,
  FormGovernanceRegistry,
  FormGovernanceWarning,
  PMReviewProcessOption,
  savePMFormDraftReviewProcessSelection,
  updatePMFormDraftSections,
} from "@/api/formAuthoringApi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Severity = "info" | "low" | "medium" | "high" | "critical" | string;

const severityClasses: Record<string, string> = {
  info: "border-slate-300 bg-slate-50 text-slate-700",
  low: "border-slate-300 bg-slate-50 text-slate-700",
  medium: "border-amber-300 bg-amber-50 text-amber-800",
  high: "border-orange-300 bg-orange-50 text-orange-800",
  critical: "border-red-300 bg-red-50 text-red-800",
};

const statusClasses: Record<string, string> = {
  draft: "border-blue-300 bg-blue-50 text-blue-800",
  validation_failed: "border-red-300 bg-red-50 text-red-800",
  review_required: "border-amber-300 bg-amber-50 text-amber-800",
  publishable: "border-emerald-300 bg-emerald-50 text-emerald-800",
  published: "border-emerald-300 bg-emerald-50 text-emerald-800",
  archived: "border-slate-300 bg-slate-50 text-slate-700",
};

const formatLabel = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value).replace(/_/g, " ");
};

const severityBadgeClass = (severity: Severity): string =>
  severityClasses[String(severity)] ?? severityClasses.info;

const statusBadgeClass = (status: string): string =>
  statusClasses[String(status)] ?? statusClasses.draft;

const sectionFieldKeys = (section: FormDraftSection): string[] => {
  const maybeLegacyFields = (section as unknown as { fields?: unknown }).fields;
  if (Array.isArray(section.field_keys)) return section.field_keys;
  if (Array.isArray(maybeLegacyFields)) return maybeLegacyFields.filter((field): field is string => typeof field === "string");
  return [];
};

const fieldCount = (sections: FormDraftSection[]): number =>
  sections.reduce((total, section) => total + sectionFieldKeys(section).length, 0);

const cloneSections = (sections: FormDraftSection[]): FormDraftSection[] =>
  sections.map((section) => ({
    section_key: section.section_key,
    title: section.title,
    field_keys: [...sectionFieldKeys(section)],
  }));

const uniqueSectionKey = (sections: FormDraftSection[]): string => {
  let index = sections.length + 1;
  const existing = new Set(sections.map((section) => section.section_key));
  while (existing.has(`section_${index}`)) index += 1;
  return `section_${index}`;
};

const getFieldLabel = (
  fieldKey: string,
  fieldMap: Map<string, CanonicalField>
): string => fieldMap.get(fieldKey)?.label ?? formatLabel(fieldKey);

const companionSummary = (
  field: CanonicalField,
  selectedFieldKeys: Set<string>
) => {
  const required = field.dependency_field_keys;
  const recommended = field.compatible_field_keys.filter(
    (fieldKey) => !required.includes(fieldKey)
  );
  return {
    required,
    recommended,
    missingRequired: required.filter((fieldKey) => !selectedFieldKeys.has(fieldKey)),
    addedRequired: required.filter((fieldKey) => selectedFieldKeys.has(fieldKey)),
    missingRecommended: recommended.filter((fieldKey) => !selectedFieldKeys.has(fieldKey)),
    addedRecommended: recommended.filter((fieldKey) => selectedFieldKeys.has(fieldKey)),
  };
};

const sortedWarnings = (warnings: FormGovernanceWarning[]): FormGovernanceWarning[] => {
  const rank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
  return [...warnings].sort(
    (a, b) => (rank[b.severity] ?? 0) - (rank[a.severity] ?? 0)
  );
};

interface PMGovernanceMessage {
  problem: string;
  whyItMatters: string;
  whatToDo: string;
}

const readableFieldList = (text: string): string => {
  const fieldText = text
    .replace(/[.]/g, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return fieldText || "the missing required fields";
};

const afterPhrase = (text: string, phrase: string): string => {
  const index = text.toLowerCase().indexOf(phrase.toLowerCase());
  if (index === -1) return "";
  return text.slice(index + phrase.length).trim();
};

const translateGovernanceWarning = (
  warning: FormGovernanceWarning,
  hasSelectedReviewProcess = false
): PMGovernanceMessage => {
  const id = warning.warning_id.toLowerCase();
  const title = warning.title.toLowerCase();
  const description = warning.description.toLowerCase();
  const combined = `${id} ${title} ${description}`;

  if (combined.includes("workflow_config_missing")) {
    if (hasSelectedReviewProcess) {
      return {
        problem: "The review process is selected, but it has not been provisioned for this scholarship yet.",
        whyItMatters:
          "The PM has chosen the review path, but the system still needs an active workflow for this program year before applications can move through review.",
        whatToDo:
          "Ask Super Admin to provision the selected review process for this scholarship before launch.",
      };
    }
    return {
      problem: "This form is not connected to a review process yet.",
      whyItMatters:
        "After students submit, the system needs to know which review path, owner, and next step should handle each application.",
      whatToDo:
        "Choose an approved review process for this scholarship. Admin will activate it before launch.",
    };
  }

  if (combined.includes("diligence_fields_without_documents")) {
    return {
      problem: "Some review fields need supporting documents before launch.",
      whyItMatters:
        "Application reviewers will need evidence for fields like identity, income, or academic details before they can complete diligence.",
      whatToDo:
        hasSelectedReviewProcess
          ? "Ask Super Admin to provision the workflow and attach the required document rules for this program."
          : "Choose a review process first, then ask Super Admin to attach the required document rules.",
    };
  }

  if (combined.includes("workflow")) {
    return {
      problem: "The review process setup is not complete yet.",
      whyItMatters:
        "Applications need an active review path before reviewers can take action.",
      whatToDo:
        hasSelectedReviewProcess
          ? "Ask Super Admin to provision the selected review process before launch."
          : "Choose an approved review process for this scholarship.",
    };
  }

  if (combined.includes("required canonical") || combined.includes("foundational canonical")) {
    const missing = readableFieldList(afterPhrase(warning.description, "fields:"));
    return {
      problem: `Required application fields are missing: ${missing}.`,
      whyItMatters:
        "Students may submit applications that reviewers cannot evaluate consistently.",
      whatToDo:
        "Add the missing fields from the Canonical Field Picker before sending this form for approval.",
    };
  }

  if (combined.includes("duplicate")) {
    return {
      problem: "This form may not reliably prevent duplicate applications.",
      whyItMatters:
        "Without a strong identity field, the same student may be able to submit more than one application.",
      whatToDo:
        "Add Aadhaar number or phone number before sending this form for approval.",
    };
  }

  if (combined.includes("document requirement") || combined.includes("diligence")) {
    return {
      problem: "Some information needs verification, but supporting documents are not configured.",
      whyItMatters:
        "Reviewers may be asked to verify student claims without having the documents needed to check them.",
      whatToDo:
        "Ask an admin to bind document requirements for this program before this form is used by students.",
    };
  }

  if (combined.includes("semantic compatibility") || combined.includes("companion")) {
    return {
      problem: "This form may not collect enough related information for scholarship review.",
      whyItMatters:
        "One selected field usually works best with companion fields, so reviewers may miss context needed for a fair decision.",
      whatToDo:
        "Review the suggested companion fields and add the ones needed for this scholarship.",
    };
  }

  if (combined.includes("dependency")) {
    return {
      problem: "A selected field depends on another field that is missing.",
      whyItMatters:
        "The form may collect partial information that cannot be interpreted correctly.",
      whatToDo:
        "Add the related missing field shown in the warning before sending this form for approval.",
    };
  }

  if (combined.includes("deprecated") || combined.includes("replacement")) {
    return {
      problem: "This form uses a field that should no longer be used.",
      whyItMatters:
        "Old fields can create inconsistent reporting, review, or applicant records.",
      whatToDo:
        "Replace it with the recommended current field before sending this form for approval.",
    };
  }

  if (combined.includes("finance") || combined.includes("bank") || combined.includes("disbursal")) {
    return {
      problem: "This change affects payment or bank-review information.",
      whyItMatters:
        "Payment-related fields can affect finance checks, disbursal readiness, and audit review.",
      whatToDo:
        "Keep the severity visible and make sure finance review is included before approval.",
    };
  }

  if (combined.includes("pii") || combined.includes("sensitive")) {
    return {
      problem: "This form collects sensitive student information.",
      whyItMatters:
        "Sensitive data requires careful review so it is collected only when needed and handled safely.",
      whatToDo:
        "Confirm the field is necessary and keep the required reviewer approval before use.",
    };
  }

  if (combined.includes("schema") && combined.includes("properties")) {
    return {
      problem: "This form does not contain any usable application fields yet.",
      whyItMatters:
        "Students would see an empty or incomplete form, and reviewers would receive no meaningful application data.",
      whatToDo:
        "Add fields from the Canonical Field Picker, then save the draft again.",
    };
  }

  return {
    problem: formatLabel(warning.title),
    whyItMatters:
      "This item may affect whether the form can be reviewed, approved, or safely shown to students.",
    whatToDo:
      warning.recommended_action
        ? formatLabel(warning.recommended_action)
        : "Review this item before sending the form for approval.",
  };
};

const defaultSection = (): FormDraftSection => ({
  section_key: "main",
  title: "Main",
  field_keys: [],
});

const PMFormAuthoringWorkspace: React.FC = () => {
  const [registry, setRegistry] = useState<FormGovernanceRegistry | null>(null);
  const [drafts, setDrafts] = useState<FormDraftProjection[]>([]);
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(null);
  const [sections, setSections] = useState<FormDraftSection[]>([defaultSection()]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingReviewProcess, setSavingReviewProcess] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [draftName, setDraftName] = useState("Governed Draft");
  const [programCode, setProgramCode] = useState("");
  const [academicYear, setAcademicYear] = useState("2026");
  const [sourceFormId, setSourceFormId] = useState<string>("");
  const [fieldSearch, setFieldSearch] = useState("");
  const [targetSectionKey, setTargetSectionKey] = useState("main");
  const [reviewProcessOptions, setReviewProcessOptions] = useState<PMReviewProcessOption[]>([]);
  const [selectedReviewTemplateId, setSelectedReviewTemplateId] = useState("");
  const [reviewSnapshot, setReviewSnapshot] =
    useState<FormApprovalEvidenceSnapshot | null>(null);
  const [concurrence, setConcurrence] =
    useState<FormApprovalConcurrenceState | null>(null);

  const selectedDraft = useMemo(
    () => drafts.find((draft) => draft.id === selectedDraftId) ?? null,
    [drafts, selectedDraftId]
  );

  const fieldMap = useMemo(() => {
    const map = new Map<string, CanonicalField>();
    (registry?.canonical_fields ?? []).forEach((field) => {
      map.set(field.field_key, field);
    });
    return map;
  }, [registry]);

  const selectedFieldKeys = useMemo(
    () => new Set(sections.flatMap((section) => sectionFieldKeys(section))),
    [sections]
  );

  const filteredFields = useMemo(() => {
    const query = fieldSearch.trim().toLowerCase();
    return (registry?.canonical_fields ?? [])
      .filter((field) => field.active && field.lifecycle_state === "active")
      .filter((field) => {
        if (!query) return true;
        return [
          field.field_key,
          field.label,
          field.category,
          field.governance_domain,
          ...field.owner_domains,
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
  }, [fieldSearch, registry]);

  const sourceForms = registry?.forms ?? [];

  const loadWorkspace = async () => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const [registryResponse, draftResponse, reviewProcessResponse] = await Promise.all([
        fetchPMFormGovernanceRegistry(),
        fetchPMFormDrafts(),
        fetchPMReviewProcessOptions(),
      ]);
      setRegistry(registryResponse);
      setDrafts(draftResponse);
      setReviewProcessOptions(reviewProcessResponse);
      const firstDraft = draftResponse[0] ?? null;
      setSelectedDraftId((current) => current ?? firstDraft?.id ?? null);
      if (!programCode && registryResponse.forms[0]?.program_code) {
        setProgramCode(registryResponse.forms[0].program_code ?? "");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load governed form authoring workspace.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedDraft) return;
    const nextSections = cloneSections(
      selectedDraft.sections.length > 0 ? selectedDraft.sections : [defaultSection()]
    );
    setSections(nextSections);
    setTargetSectionKey(nextSections[0]?.section_key ?? "main");
    setSelectedReviewTemplateId(
      selectedDraft.selected_review_process?.workflow_template_id
        ? String(selectedDraft.selected_review_process.workflow_template_id)
        : ""
    );
    setReviewSnapshot(null);
    setConcurrence(null);
    setDirty(false);
  }, [selectedDraft]);

  useEffect(() => {
    if (!selectedDraft) return;
    let cancelled = false;
    const loadReviewProcessOptions = async () => {
      try {
        const options = await fetchPMReviewProcessOptions({
          program_code: selectedDraft.program_code,
          academic_year: selectedDraft.academic_year,
        });
        if (!cancelled) {
          setReviewProcessOptions(options);
        }
      } catch (err) {
        console.error(err);
      }
    };
    void loadReviewProcessOptions();
    return () => {
      cancelled = true;
    };
  }, [selectedDraft]);

  const refreshSelectedDraft = (updated: FormDraftProjection) => {
    setDrafts((prev) =>
      prev.map((draft) => (draft.id === updated.id ? updated : draft))
    );
    setSelectedDraftId(updated.id);
    setSections(cloneSections(updated.sections));
    setDirty(false);
    setReviewSnapshot(null);
    setConcurrence(null);
  };

  const createDraft = async () => {
    const cleanProgram = programCode.trim();
    const cleanYear = academicYear.trim();
    if (!cleanProgram || !cleanYear) {
      setError("Program code and academic year are required.");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const created = await createPMFormDraft({
        program_code: cleanProgram,
        academic_year: cleanYear,
        name: draftName.trim() || "Governed Draft",
        source_student_form_id: sourceFormId ? Number(sourceFormId) : null,
        sections: sourceFormId ? undefined : [defaultSection()],
      });
      setDrafts((prev) => [created, ...prev]);
      refreshSelectedDraft(created);
      setNotice("Draft created.");
    } catch (err: unknown) {
      console.error(err);
      setError("Draft creation failed. Check workflow binding and canonical fields.");
    } finally {
      setSaving(false);
    }
  };

  const saveSections = async () => {
    if (!selectedDraft) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const updated = await updatePMFormDraftSections(selectedDraft.id, { sections });
      refreshSelectedDraft(updated);
      setNotice("Draft saved.");
    } catch (err) {
      console.error(err);
      setError("Draft save failed. Backend governance validation rejected the update.");
    } finally {
      setSaving(false);
    }
  };

  const saveReviewProcessSelection = async () => {
    if (!selectedDraft || !selectedReviewTemplateId) return;
    setSavingReviewProcess(true);
    setError("");
    setNotice("");
    try {
      const updated = await savePMFormDraftReviewProcessSelection(selectedDraft.id, {
        workflow_template_id: Number(selectedReviewTemplateId),
      });
      refreshSelectedDraft(updated);
      setNotice("Review process saved. Super Admin must provision it before launch.");
    } catch (err) {
      console.error(err);
      setError("Review process save failed. Choose an active approved review process.");
    } finally {
      setSavingReviewProcess(false);
    }
  };

  const freezeReviewEvidence = async () => {
    if (!selectedDraft) return;
    setFreezing(true);
    setError("");
    setNotice("");
    try {
      const snapshot = await createPMFormDraftReviewSnapshot(selectedDraft.id);
      setReviewSnapshot(snapshot);
      const state = await fetchFormApprovalConcurrence(snapshot.id);
      setConcurrence(state);
      setNotice("Review evidence saved.");
    } catch (err) {
      console.error(err);
      setError("Review evidence freeze failed. Save the draft and resolve blockers first.");
    } finally {
      setFreezing(false);
    }
  };

  const updateSection = (
    sectionKey: string,
    patch: Partial<FormDraftSection>
  ) => {
    setSections((prev) =>
      prev.map((section) =>
        section.section_key === sectionKey ? { ...section, ...patch } : section
      )
    );
    setDirty(true);
  };

  const addSection = () => {
    const sectionKey = uniqueSectionKey(sections);
    setSections((prev) => [
      ...prev,
      { section_key: sectionKey, title: "New Section", field_keys: [] },
    ]);
    setTargetSectionKey(sectionKey);
    setDirty(true);
  };

  const moveSection = (sectionKey: string, direction: -1 | 1) => {
    setSections((prev) => {
      const index = prev.findIndex((section) => section.section_key === sectionKey);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const next = cloneSections(prev);
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  const addField = (fieldKey: string) => {
    if (selectedFieldKeys.has(fieldKey)) return;
    setSections((prev) =>
      prev.map((section) =>
        section.section_key === targetSectionKey
          ? { ...section, field_keys: [...sectionFieldKeys(section), fieldKey] }
          : section
      )
    );
    setDirty(true);
  };

  const addFields = (fieldKeys: string[]) => {
    const uniqueMissing = fieldKeys.filter((fieldKey) => !selectedFieldKeys.has(fieldKey));
    if (uniqueMissing.length === 0) return;
    setSections((prev) =>
      prev.map((section) =>
        section.section_key === targetSectionKey
          ? {
              ...section,
              field_keys: [...sectionFieldKeys(section), ...uniqueMissing],
            }
          : section
      )
    );
    setDirty(true);
  };

  const removeField = (sectionKey: string, fieldKey: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.section_key === sectionKey
          ? {
              ...section,
              field_keys: sectionFieldKeys(section).filter((key) => key !== fieldKey),
            }
          : section
      )
    );
    setDirty(true);
  };

  const moveField = (sectionKey: string, fieldKey: string, direction: -1 | 1) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.section_key !== sectionKey) return section;
        const fields = sectionFieldKeys(section);
        const index = fields.indexOf(fieldKey);
        const target = index + direction;
        if (index < 0 || target < 0 || target >= fields.length) {
          return section;
        }
        const nextFields = [...fields];
        [nextFields[index], nextFields[target]] = [nextFields[target], nextFields[index]];
        return { ...section, field_keys: nextFields };
      })
    );
    setDirty(true);
  };

  const removeSection = (sectionKey: string) => {
    if (sections.length === 1) return;
    setSections((prev) => prev.filter((section) => section.section_key !== sectionKey));
    if (targetSectionKey === sectionKey) {
      const fallback = sections.find((section) => section.section_key !== sectionKey);
      setTargetSectionKey(fallback?.section_key ?? "main");
    }
    setDirty(true);
  };

  return (
    <div className="w-full max-w-[1440px] space-y-4 overflow-x-hidden p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-950">
            Scholarship Form Builder
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Build the scholarship form, choose its review process, and check readiness before approval.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadWorkspace} disabled={loading}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {notice ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-800">
          {notice}
        </div>
      ) : null}

      {loading ? (
        <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
          <Skeleton className="h-[520px] w-full" />
          <Skeleton className="h-[520px] w-full" />
        </div>
      ) : (
        <div className="grid min-w-0 gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
          <DraftListPanel
            drafts={drafts}
            selectedDraftId={selectedDraftId}
            onSelect={setSelectedDraftId}
            draftName={draftName}
            setDraftName={setDraftName}
            programCode={programCode}
            setProgramCode={setProgramCode}
            academicYear={academicYear}
            setAcademicYear={setAcademicYear}
            sourceFormId={sourceFormId}
            setSourceFormId={setSourceFormId}
            sourceForms={sourceForms}
            onCreate={createDraft}
            saving={saving}
          />

          <div className="min-w-0 space-y-4">
            {selectedDraft ? (
              <>
                <DraftSummary draft={selectedDraft} dirty={dirty} />

                <ReviewProcessPanel
                  draft={selectedDraft}
                  options={reviewProcessOptions}
                  selectedReviewTemplateId={selectedReviewTemplateId}
                  setSelectedReviewTemplateId={setSelectedReviewTemplateId}
                  onSave={saveReviewProcessSelection}
                  saving={savingReviewProcess}
                />

                <div className="grid min-w-0 items-start gap-4 2xl:grid-cols-[minmax(0,1fr)_360px]">
                  <DraftEditorPanel
                    sections={sections}
                    fieldMap={fieldMap}
                    targetSectionKey={targetSectionKey}
                    setTargetSectionKey={setTargetSectionKey}
                    onAddSection={addSection}
                    onRemoveSection={removeSection}
                    onMoveSection={moveSection}
                    onUpdateSection={updateSection}
                    onRemoveField={removeField}
                    onMoveField={moveField}
                    onSave={saveSections}
                    saving={saving}
                    dirty={dirty}
                  />

                  <CanonicalFieldPicker
                    fields={filteredFields}
                    fieldMap={fieldMap}
                    fieldSearch={fieldSearch}
                    setFieldSearch={setFieldSearch}
                    selectedFieldKeys={selectedFieldKeys}
                    targetSectionKey={targetSectionKey}
                    onAddField={addField}
                    onAddFields={addFields}
                  />
                </div>

                <div className="grid min-w-0 items-start gap-4 2xl:grid-cols-[minmax(0,1fr)_360px]">
                  <GovernanceWarningsPanel draft={selectedDraft} />
                  <DiffPanel draft={selectedDraft} />
                </div>

                <div className="grid min-w-0 items-start gap-4 2xl:grid-cols-[minmax(0,1fr)_360px]">
                  <ApprovalPanel
                    draft={selectedDraft}
                    snapshot={reviewSnapshot}
                    concurrence={concurrence}
                    onFreeze={freezeReviewEvidence}
                    freezing={freezing}
                    dirty={dirty}
                  />
                  <ApplicantPreviewPanel sections={sections} fieldMap={fieldMap} />
                </div>
              </>
            ) : (
              <Card>
                <CardContent className="py-10 text-sm text-muted-foreground">
                  Create or select a governed draft to begin PM authoring.
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface DraftListPanelProps {
  drafts: FormDraftProjection[];
  selectedDraftId: number | null;
  onSelect: (draftId: number) => void;
  draftName: string;
  setDraftName: (value: string) => void;
  programCode: string;
  setProgramCode: (value: string) => void;
  academicYear: string;
  setAcademicYear: (value: string) => void;
  sourceFormId: string;
  setSourceFormId: (value: string) => void;
  sourceForms: FormGovernanceRegistry["forms"];
  onCreate: () => void;
  saving: boolean;
}

const DraftListPanel: React.FC<DraftListPanelProps> = ({
  drafts,
  selectedDraftId,
  onSelect,
  draftName,
  setDraftName,
  programCode,
  setProgramCode,
  academicYear,
  setAcademicYear,
  sourceFormId,
  setSourceFormId,
  sourceForms,
  onCreate,
  saving,
}) => (
  <div className="space-y-4">
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Plus className="h-4 w-4" />
          New Draft
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          value={draftName}
          onChange={(event) => setDraftName(event.target.value)}
          placeholder="Draft name"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            value={programCode}
            onChange={(event) => setProgramCode(event.target.value)}
            placeholder="Program code"
          />
          <Input
            value={academicYear}
            onChange={(event) => setAcademicYear(event.target.value)}
            placeholder="Academic year"
          />
        </div>
        <select
          value={sourceFormId}
          onChange={(event) => setSourceFormId(event.target.value)}
          className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
        >
          <option value="">Start from empty governed draft</option>
          {sourceForms.map((form) => (
            <option key={form.form_id} value={form.form_id}>
              {form.name} - {form.program_code ?? "no program"} - {form.version}
            </option>
          ))}
        </select>
        <Button className="w-full" onClick={onCreate} disabled={saving}>
          <FileText className="mr-2 h-4 w-4" />
          Create Draft
        </Button>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle className="text-base">Drafts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {drafts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No governed form drafts exist for this tenant workspace.
          </p>
        ) : null}
        {drafts.map((draft) => (
          <button
            key={draft.id}
            type="button"
            onClick={() => onSelect(draft.id)}
            className={`w-full rounded-md border p-3 text-left transition ${
              selectedDraftId === draft.id
                ? "border-indigo-500 bg-indigo-50"
                : "border-gray-200 bg-white hover:border-gray-300"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-gray-950">
                  {draft.name}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {draft.program_code} - {draft.academic_year}
                </div>
              </div>
              <Badge className={statusBadgeClass(draft.status)} variant="outline">
                {formatLabel(draft.status)}
              </Badge>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge
                className={severityBadgeClass(draft.diff.governance_review_severity)}
                variant="outline"
              >
                {formatLabel(draft.diff.governance_review_severity)}
              </Badge>
              <Badge variant="outline">
                {draft.validation.publishability.verdict}
              </Badge>
              <Badge variant="outline">
                {draft.applicant_visible ? "applicant visible" : "applicant hidden"}
              </Badge>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {formatLabel(draft.diff.reviewer_action_required)} - updated{" "}
              {draft.updated_at ? new Date(draft.updated_at).toLocaleString() : "-"}
            </div>
          </button>
        ))}
      </CardContent>
    </Card>
  </div>
);

const DraftSummary: React.FC<{ draft: FormDraftProjection; dirty: boolean }> = ({
  draft,
  dirty,
}) => (
  <div className="rounded-md border bg-white p-4">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-950">{draft.name}</h2>
          {dirty ? (
            <Badge className="border-amber-300 bg-amber-50 text-amber-800" variant="outline">
              unsaved section edits
            </Badge>
          ) : null}
        </div>
        <div className="mt-1 text-sm text-muted-foreground">
          Draft #{draft.id} - {draft.program_code} - {draft.academic_year}
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          PM selected review process:{" "}
          <span className="font-medium text-gray-950">
            {draft.selected_review_process?.name ?? "Not selected"}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Badge className={statusBadgeClass(draft.status)} variant="outline">
          {formatLabel(draft.status)}
        </Badge>
        <Badge
          className={severityBadgeClass(draft.diff.governance_review_severity)}
          variant="outline"
        >
          {formatLabel(draft.diff.governance_review_severity)}
        </Badge>
        <Badge variant="outline">
          {draft.publish_blocked ? "publish blocked" : "publishability clean"}
        </Badge>
        <Badge className="border-slate-300 bg-slate-50 text-slate-700" variant="outline">
          activation unavailable
        </Badge>
      </div>
    </div>
  </div>
);

interface ReviewProcessPanelProps {
  draft: FormDraftProjection;
  options: PMReviewProcessOption[];
  selectedReviewTemplateId: string;
  setSelectedReviewTemplateId: (value: string) => void;
  onSave: () => void;
  saving: boolean;
}

const ReviewProcessPanel: React.FC<ReviewProcessPanelProps> = ({
  draft,
  options,
  selectedReviewTemplateId,
  setSelectedReviewTemplateId,
  onSave,
  saving,
}) => {
  const selectedOption = options.find(
    (option) => String(option.id) === selectedReviewTemplateId
  );
  const savedSelection = draft.selected_review_process;
  const selectionChanged =
    selectedReviewTemplateId !==
    (savedSelection?.workflow_template_id
      ? String(savedSelection.workflow_template_id)
      : "");
  const selectionBlocked =
    selectedOption?.available_for_selection === false ||
    (options.length > 0 && options.every((option) => !option.available_for_selection));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardCheck className="h-4 w-4" />
          Review Process
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
          Choose an approved review process for this scholarship. Admin will activate it before launch.
        </div>
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
          <select
            value={selectedReviewTemplateId}
            onChange={(event) => setSelectedReviewTemplateId(event.target.value)}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
          >
            <option value="">Select approved review process</option>
            {options.map((option) => (
              <option
                key={option.id}
                value={option.id}
                disabled={!option.available_for_selection}
              >
                {option.path_label ?? `${option.name} v${option.version}`}
              </option>
            ))}
          </select>
          <Button
            onClick={onSave}
            disabled={!selectedReviewTemplateId || saving || selectionBlocked || !selectionChanged}
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving..." : "Save Review Process"}
          </Button>
        </div>
        {savedSelection ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge className="border-emerald-300 bg-emerald-50 text-emerald-800" variant="outline">
              selected
            </Badge>
            <span className="font-medium text-gray-950">{savedSelection.name}</span>
            <span className="text-muted-foreground">v{savedSelection.version}</span>
          </div>
        ) : (
          <div className="text-sm text-amber-700">
            Step 3 is incomplete until a review process is saved.
          </div>
        )}
        {selectionBlocked ? (
          <div className="text-sm text-muted-foreground">
            Admin has already provisioned this review process setup for the program year.
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

interface DraftEditorPanelProps {
  sections: FormDraftSection[];
  fieldMap: Map<string, CanonicalField>;
  targetSectionKey: string;
  setTargetSectionKey: (value: string) => void;
  onAddSection: () => void;
  onRemoveSection: (sectionKey: string) => void;
  onMoveSection: (sectionKey: string, direction: -1 | 1) => void;
  onUpdateSection: (sectionKey: string, patch: Partial<FormDraftSection>) => void;
  onRemoveField: (sectionKey: string, fieldKey: string) => void;
  onMoveField: (sectionKey: string, fieldKey: string, direction: -1 | 1) => void;
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
}

const DraftEditorPanel: React.FC<DraftEditorPanelProps> = ({
  sections,
  fieldMap,
  targetSectionKey,
  setTargetSectionKey,
  onAddSection,
  onRemoveSection,
  onMoveSection,
  onUpdateSection,
  onRemoveField,
  onMoveField,
  onSave,
  saving,
  dirty,
}) => (
  <Card>
    <CardHeader>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle className="text-base">Draft Editor</CardTitle>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onAddSection}>
            <Plus className="mr-2 h-4 w-4" />
            Section
          </Button>
          <Button size="sm" onClick={onSave} disabled={saving || !dirty}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
        Canonical fields only. Governance metadata, dependencies, OCR rules, and
        workflow bindings are inherited from backend authority.
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Field picker target</span>
        <select
          value={targetSectionKey}
          onChange={(event) => setTargetSectionKey(event.target.value)}
          className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
        >
          {sections.map((section, index) => (
            <option key={`${section.section_key}-${index}`} value={section.section_key}>
              {section.title}
            </option>
          ))}
        </select>
      </div>

      {sections.map((section, sectionIndex) => (
        <div key={`${section.section_key}-${sectionIndex}`} className="rounded-md border bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-[240px] flex-1 items-center gap-2">
              <Input
                value={section.title}
                onChange={(event) =>
                  onUpdateSection(section.section_key, { title: event.target.value })
                }
                className="font-medium"
              />
              <Badge variant="outline">{sectionFieldKeys(section).length} fields</Badge>
            </div>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onMoveSection(section.section_key, -1)}
                disabled={sectionIndex === 0}
                title="Move section up"
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onMoveSection(section.section_key, 1)}
                disabled={sectionIndex === sections.length - 1}
                title="Move section down"
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRemoveSection(section.section_key)}
                disabled={sections.length === 1}
                title="Remove section"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="mt-3 space-y-2">
            {sectionFieldKeys(section).length === 0 ? (
              <div className="rounded border border-dashed border-gray-300 p-3 text-sm text-muted-foreground">
                No canonical fields assigned to this section.
              </div>
            ) : null}
            {sectionFieldKeys(section).map((fieldKey, fieldIndex) => {
              const field = fieldMap.get(fieldKey);
              const fields = sectionFieldKeys(section);
              return (
                <div
                  key={`${fieldKey}-${fieldIndex}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded border bg-slate-50 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-950">
                      {getFieldLabel(fieldKey, fieldMap)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {fieldKey} - {field?.category ?? "uncategorized"}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {field?.metadata.finance_sensitive ? (
                      <Badge className="border-orange-300 bg-orange-50 text-orange-800" variant="outline">
                        finance
                      </Badge>
                    ) : null}
                    {field?.metadata.required_for_diligence ? (
                      <Badge className="border-blue-300 bg-blue-50 text-blue-800" variant="outline">
                        diligence
                      </Badge>
                    ) : null}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onMoveField(section.section_key, fieldKey, -1)}
                      disabled={fieldIndex === 0}
                      title="Move field up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onMoveField(section.section_key, fieldKey, 1)}
                      disabled={fieldIndex === fields.length - 1}
                      title="Move field down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRemoveField(section.section_key, fieldKey)}
                      title="Remove field"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

interface CanonicalFieldPickerProps {
  fields: CanonicalField[];
  fieldMap: Map<string, CanonicalField>;
  fieldSearch: string;
  setFieldSearch: (value: string) => void;
  selectedFieldKeys: Set<string>;
  targetSectionKey: string;
  onAddField: (fieldKey: string) => void;
  onAddFields: (fieldKeys: string[]) => void;
}

const CanonicalFieldPicker: React.FC<CanonicalFieldPickerProps> = ({
  fields,
  fieldMap,
  fieldSearch,
  setFieldSearch,
  selectedFieldKeys,
  targetSectionKey,
  onAddField,
  onAddFields,
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base">Canonical Field Picker</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <Input
        value={fieldSearch}
        onChange={(event) => setFieldSearch(event.target.value)}
        placeholder="Search canonical key, category, owner"
      />
      <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
        {fields.map((field) => {
          const selected = selectedFieldKeys.has(field.field_key);
          const companions = companionSummary(field, selectedFieldKeys);
          return (
            <div key={field.field_key} className="rounded-md border bg-white p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-gray-950">{field.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {field.field_key} - {field.category}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={selected ? "secondary" : "outline"}
                  onClick={() => onAddField(field.field_key)}
                  disabled={selected || !targetSectionKey}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {selected ? "Added" : "Add"}
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="outline">{field.field_type}</Badge>
                <Badge variant="outline">PII {field.metadata.pii_level}</Badge>
                {field.metadata.finance_sensitive ? (
                  <Badge className="border-orange-300 bg-orange-50 text-orange-800" variant="outline">
                    finance sensitive
                  </Badge>
                ) : null}
                {field.metadata.required_for_diligence ? (
                  <Badge className="border-blue-300 bg-blue-50 text-blue-800" variant="outline">
                    diligence required
                  </Badge>
                ) : null}
                {field.dependency_field_keys.length > 0 ? (
                  <Badge className="border-amber-300 bg-amber-50 text-amber-800" variant="outline">
                    has required companions
                  </Badge>
                ) : null}
                {field.compatible_field_keys.length > 0 ? (
                  <Badge className="border-sky-300 bg-sky-50 text-sky-800" variant="outline">
                    has recommended companions
                  </Badge>
                ) : null}
              </div>
              {companions.required.length > 0 ? (
                <div
                  className={`mt-3 rounded-md border p-2 text-xs ${
                    companions.missingRequired.length > 0
                      ? "border-orange-300 bg-orange-50 text-orange-900"
                      : "border-emerald-200 bg-emerald-50 text-emerald-800"
                  }`}
                >
                  <div className="font-semibold">
                    Required companions
                  </div>
                  <div className="mt-1">
                    {field.label} needs{" "}
                    {companions.required
                      .map((fieldKey) => getFieldLabel(fieldKey, fieldMap))
                      .join(", ")}
                    .
                  </div>
                  {companions.missingRequired.length > 0 ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span>
                        Missing:{" "}
                        {companions.missingRequired
                          .map((fieldKey) => getFieldLabel(fieldKey, fieldMap))
                          .join(", ")}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => onAddFields(companions.missingRequired)}
                        disabled={!targetSectionKey}
                        className="h-7 border-orange-300 bg-white text-orange-900 hover:bg-orange-100"
                      >
                        Add missing required fields
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-1 font-medium">All required companions are already added.</div>
                  )}
                </div>
              ) : null}
              {companions.recommended.length > 0 ? (
                <div className="mt-3 rounded-md border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900">
                  <div className="font-semibold">Recommended companions</div>
                  <div className="mt-1">
                    {field.label} works best with{" "}
                    {companions.recommended
                      .map((fieldKey) => getFieldLabel(fieldKey, fieldMap))
                      .join(", ")}
                    .
                  </div>
                  <div className="mt-1">
                    {companions.missingRecommended.length > 0
                      ? `Optional to add: ${companions.missingRecommended
                          .map((fieldKey) => getFieldLabel(fieldKey, fieldMap))
                          .join(", ")}`
                      : "All recommended companions are already added."}
                  </div>
                </div>
              ) : null}
              <div className="mt-2 text-xs text-muted-foreground">
                Owners: {field.owner_domains.join(", ") || "-"} - Dependency severity:{" "}
                {Object.entries(field.dependency_severity)
                  .map(([key, severity]) => `${key}:${severity}`)
                  .join(", ") || "-"}
              </div>
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
);

const GovernanceWarningsPanel: React.FC<{ draft: FormDraftProjection }> = ({
  draft,
}) => {
  const warnings = sortedWarnings(draft.validation.warnings);
  const hasSelectedReviewProcess = Boolean(draft.selected_review_process);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4" />
          Readiness Checks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-3">
          <Metric label="Launch status" value={draft.validation.publishability.verdict} />
          <Metric
            label="Must fix"
            value={draft.validation.publishability.blocking_issue_count}
          />
          <Metric
            label="Advisories"
            value={draft.validation.publishability.warning_issue_count}
          />
        </div>
        {warnings.length === 0 ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            No readiness issues reported by backend validation.
          </div>
        ) : null}
        <div className="space-y-2">
          {warnings.map((warning) => {
            const message = translateGovernanceWarning(warning, hasSelectedReviewProcess);
            return (
              <div
                key={warning.warning_id}
                className={`rounded-md border p-3 ${severityBadgeClass(warning.severity)}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={severityBadgeClass(warning.severity)} variant="outline">
                    {warning.severity}
                  </Badge>
                  <span className="text-sm font-medium">{message.problem}</span>
                </div>
                <div className="mt-3 space-y-2 text-sm">
                  <div>
                    <div className="text-xs font-semibold uppercase text-current opacity-75">
                      Why it matters
                    </div>
                    <p>{message.whyItMatters}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-current opacity-75">
                      What to do
                    </div>
                    <p className="font-medium">{message.whatToDo}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const Metric: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => (
  <div className="rounded-md border bg-white p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="mt-1 text-sm font-semibold text-gray-950">
      {formatLabel(value)}
    </div>
  </div>
);

const DiffPanel: React.FC<{ draft: FormDraftProjection }> = ({ draft }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <GitCompare className="h-4 w-4" />
        Diff Projection
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Badge
          className={severityBadgeClass(draft.diff.governance_review_severity)}
          variant="outline"
        >
          {formatLabel(draft.diff.governance_review_severity)}
        </Badge>
        <Badge variant="outline">{formatLabel(draft.diff.risk_level)}</Badge>
        <Badge variant="outline">
          {formatLabel(draft.diff.reviewer_action_required)}
        </Badge>
      </div>
      <DiffList label="Fields added" values={draft.diff.fields_added} />
      <DiffList label="Fields removed" values={draft.diff.fields_removed} />
      <DiffList label="Sections added" values={draft.diff.sections_added} />
      <DiffList label="Sections removed" values={draft.diff.sections_removed} />
      <DiffList
        label="Workflow domains changed"
        values={draft.diff.workflow_requirement_domain_delta}
      />
      <DiffList label="Severity reasons" values={draft.diff.severity_reasons} />
      <div className="rounded-md border bg-slate-50 p-3 text-xs text-slate-700">
        Final approval roles:{" "}
        {(draft.diff.approval_authority.final_approval_roles ?? []).join(", ") || "-"}
        <br />
        Supporting concurrence:{" "}
        {(draft.diff.approval_authority.required_supporting_roles ?? []).join(", ") || "-"}
      </div>
    </CardContent>
  </Card>
);

const DiffList: React.FC<{ label: string; values: string[] }> = ({ label, values }) => (
  <div>
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    <div className="mt-1 flex flex-wrap gap-2">
      {values.length > 0 ? (
        values.map((value) => (
          <Badge key={value} variant="outline">
            {formatLabel(value)}
          </Badge>
        ))
      ) : (
        <span className="text-xs text-muted-foreground">None</span>
      )}
    </div>
  </div>
);

interface ApprovalPanelProps {
  draft: FormDraftProjection;
  snapshot: FormApprovalEvidenceSnapshot | null;
  concurrence: FormApprovalConcurrenceState | null;
  onFreeze: () => void;
  freezing: boolean;
  dirty: boolean;
}

const ApprovalPanel: React.FC<ApprovalPanelProps> = ({
  draft,
  snapshot,
  concurrence,
  onFreeze,
  freezing,
  dirty,
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <ClipboardCheck className="h-4 w-4" />
        Approval and Concurrence
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
        <div className="flex items-center gap-2 font-medium text-slate-800">
          <LockKeyhole className="h-4 w-4" />
          Activation remains unavailable in PM authoring.
        </div>
        <p className="mt-1 text-xs text-slate-700">
          This workspace can freeze review evidence, but cannot publish or activate
          applicant-visible intake.
        </p>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Requirement</TableHead>
            <TableHead>Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell>Review domains</TableCell>
            <TableCell>
              {(draft.diff.approval_authority.required_review_domains ?? []).join(", ") || "-"}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Final approval</TableCell>
            <TableCell>
              {(draft.diff.approval_authority.final_approval_roles ?? []).join(", ") || "-"}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Supporting concurrence</TableCell>
            <TableCell>
              {(draft.diff.approval_authority.required_supporting_roles ?? []).join(", ") || "-"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Button
        variant="outline"
        onClick={onFreeze}
        disabled={freezing || dirty}
        className="w-full"
      >
        <ShieldCheck className="mr-2 h-4 w-4" />
        Freeze Review Evidence
      </Button>
      {dirty ? (
        <p className="text-xs text-amber-700">
          Save section edits before freezing review evidence.
        </p>
      ) : null}
      {snapshot ? (
        <div className="rounded-md border bg-white p-3 text-sm">
          <div className="font-medium">Snapshot #{snapshot.id}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {snapshot.status} - {snapshot.created_at ?? "-"}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge
              className={severityBadgeClass(snapshot.governance_review_severity)}
              variant="outline"
            >
              {formatLabel(snapshot.governance_review_severity)}
            </Badge>
            <Badge variant="outline">
              {snapshot.activation_blocked ? "activation blocked" : "activation ready"}
            </Badge>
            <Badge variant="outline">
              {snapshot.applicant_visible ? "visible" : "hidden"}
            </Badge>
          </div>
        </div>
      ) : null}
      {concurrence ? (
        <div className="rounded-md border bg-white p-3">
          <div className="flex flex-wrap gap-2">
            <Badge
              className={
                concurrence.rejected
                  ? severityClasses.critical
                  : concurrence.activation_preconditions_satisfied
                  ? severityClasses.medium
                  : severityClasses.info
              }
              variant="outline"
            >
              {concurrence.rejected
                ? "rejected"
                : concurrence.activation_preconditions_satisfied
                ? "preconditions satisfied"
                : "concurrence pending"}
            </Badge>
            <Badge variant="outline">
              {concurrence.activation_blocked ? "activation blocked" : "activation open"}
            </Badge>
          </div>
          <div className="mt-3 space-y-2 text-xs">
            {concurrence.role_states.map((state, index) => (
              <div key={`${state.role}-${index}`} className="rounded border p-2">
                {formatLabel(String(state.role ?? "-"))}:{" "}
                {formatLabel(String(state.state ?? "pending"))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </CardContent>
  </Card>
);

const ApplicantPreviewPanel: React.FC<{
  sections: FormDraftSection[];
  fieldMap: Map<string, CanonicalField>;
}> = ({ sections, fieldMap }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <Eye className="h-4 w-4" />
        Applicant Preview
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
        Read-only rendering of the draft section order. It does not create an
        applicant route or change active intake.
      </div>
      {sections.map((section, sectionIndex) => (
        <div key={`${section.section_key}-${sectionIndex}`} className="rounded-md border bg-white p-3">
          <div className="font-medium text-gray-950">{section.title}</div>
          <div className="mt-3 space-y-2">
            {sectionFieldKeys(section).map((fieldKey, fieldIndex) => (
              <div key={`${fieldKey}-${fieldIndex}`}>
                <label className="text-xs font-medium text-gray-700">
                  {getFieldLabel(fieldKey, fieldMap)}
                </label>
                <div className="mt-1 h-9 rounded-md border bg-slate-50 px-3 py-2 text-xs text-muted-foreground">
                  {fieldMap.get(fieldKey)?.field_type ?? "field"} - immutable snapshot field
                </div>
              </div>
            ))}
            {sectionFieldKeys(section).length === 0 ? (
              <p className="text-sm text-muted-foreground">No visible fields.</p>
            ) : null}
          </div>
        </div>
      ))}
      <div className="text-xs text-muted-foreground">
        Preview field count: {fieldCount(sections)}
      </div>
    </CardContent>
  </Card>
);

export default PMFormAuthoringWorkspace;
